import React, { useEffect, useState } from 'react'

import { observer } from 'mobx-react'
import { FieldErrors, useForm } from 'react-hook-form'

import cn from 'classnames'
import * as yup from 'yup'

import { yupResolver } from '@hookform/resolvers/yup'

import { toast } from 'App'
import Button from 'components/Button'
import ButtonIcon from 'components/ButtonIcon'
import SocialLink from 'components/SocialLink'
import UserInfoInput from 'components/UserInfoInput'

import { useStore } from 'store'

import styles from './styles.module.scss'

interface IProps {
  currentUser?: boolean
}

const SignUpSchema = yup.object().shape({
  PublicEmail: yup
    .string()
    .test(
      'symbols',
      'Email is in wrong format. Please use xxx@xxxxx.com',
      (value, context) => {
        if (!value) return true
        return Boolean(
          value?.match(
            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/g
          )
        );
      }
    ),
  Website: yup.string(),
  PhoneNumber: yup.string(),
  FacebookLink: yup.string().test('symbol', 'Error facebook link', value => {
    if (!value) return true
    return Boolean(
      value?.match(/http(?:s)?:\/\/(?:www\.)?facebook\.com\/([a-zA-Z0-9_]+)/)
    );
  }),
  TwitterLink: yup.string().test('symbol', 'Error twitter link', value => {
    if (!value) return true
    return Boolean(
      value?.match(
        /http(?:s)?:\/\/(?:www\.)?(?:twitter|x)\.com\/([a-zA-Z0-9_]+)/
      )
    );
  }),
  TelegramLink: yup.string().test('symbol', 'Error telegram link', value => {
    if (!value) return true
    return Boolean(
      value?.match(
        /(https?:\/\/)?(www[.])?(telegram|t)\.me\/([a-zA-Z0-9_-]*)\/?$/
      )
      // value?.match(/^(https:\/\/t\.me\/|https:\/\/telegram\.me\/)[a-zA-Z0-9_]{3,15}[a-zA-Z0-9]$/,),
    );
  }),
  LinkedInLink: yup.string().test('symbol', 'Error linkedIn link', value => {
    if (!value) return true
    return Boolean(
      value?.match(/http(?:s)?:\/\/(?:www\.)?linkedin\.com\/([a-zA-Z0-9_]+)/)
    );
  }),
  RedditLink: yup.string().test('symbol', 'Error reddit link', value => {
    if (!value) return true
    return Boolean(
      value?.match(/http(?:s)?:\/\/(?:www\.)?reddit\.com\/([a-zA-Z0-9_]+)/)
    );
  }),
  GitLink: yup.string().test('symbol', 'Error git link', value => {
    if (!value) return true
    return Boolean(
      value?.match(/http(?:s)?:\/\/(?:www\.)?github\.com\/([a-zA-Z0-9_]+)/)
    );
  }),
  InstagramLink: yup.string().test('symbol', 'Error instagram link', value => {
    if (!value) return true
    return Boolean(
      value?.match(
        /(?:(?:http|https):\/\/)?(?:www.)?(?:instagram.com|instagr.am|instagr.com)\/(\w+)/gim
      )
    );
  }),
})

//Resize
const UserInfoContact: React.FC<IProps> = observer(({ currentUser }) => {
  const { profile } = useStore()
  const [change, setChange] = useState(false)
  const { PublicEmail, PhoneNumber, Website, Email, DeletionMark } =
    profile.viewedUser

  // useEffect(() => {
  //   setChange(!!currentUser && !Email)
  // }, [currentUser, Email])
  useEffect(() => {
    if (!currentUser) setChange(false)
  }, [currentUser])

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(SignUpSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  })

  useEffect(() => {
    let {
      FacebookLink,
      TelegramLink,
      TwitterLink,
      LinkedInLink,
      RedditLink,
      GitLink,
      InstagramLink,
    } = profile.viewedUser

    setValue('FacebookLink', FacebookLink)
    setValue('TelegramLink', TelegramLink)
    setValue('TwitterLink', TwitterLink)
    setValue('LinkedInLink', LinkedInLink)
    setValue('RedditLink', RedditLink)
    setValue('GitLink', GitLink)
    setValue('InstagramLink', InstagramLink)
  }, [profile.viewedUser])

  const onSubmit = async (data: Record<string, string>) => {
    await profile.onChangeUserInfo(data)
    setChange(false)
  }

  const onError = (err: FieldErrors) => {
    toast({
      type: 'warning',
      message: 'Some data is missing or invalid',
    })
  }

  if (DeletionMark) {
    return null
  }

  return (
    <form
      className={cn({
        [styles.cartBody]: true,
        [styles.cartBodyCurrent]: currentUser,
      })}
      onSubmit={handleSubmit(onSubmit as any, onError)}
    >
      <div className={styles.titleBlock}>
        <div className={styles.title}>Contact Info</div>
        {currentUser && (
          <ButtonIcon
            style={{ pointerEvents: change ? 'none' : 'auto' }}
            icon={'edit'}
            onClick={() => setChange(true)}
          />
        )}
      </div>
      {PublicEmail || change ? (
        <UserInfoInput
          placeholder={'Email'}
          title={'Email'}
          edit={change}
          value={PublicEmail}
          name={'PublicEmail'}
          register={register}
          error={errors.PublicEmail?.message}
          styleErrorIcon={{ padding: 0, width: '16px', height: '16px' }}
          textType={'link'}
        />
      ) : null}
      {PhoneNumber || change ? (
        <UserInfoInput
          placeholder={'Mobile'}
          title={'Mobile'}
          edit={change}
          value={PhoneNumber}
          name={'PhoneNumber'}
          register={register}
          error={errors.PhoneNumber?.message}
          styleErrorIcon={{ padding: 0, width: '16px', height: '16px' }}
        />
      ) : null}
      {Website || change ? (
        <UserInfoInput
          placeholder={'Web Site'}
          title={'Website'}
          edit={change}
          value={Website}
          name={'Website'}
          register={register}
          error={errors.Website?.message}
          styleErrorIcon={{ padding: 0, width: '16px', height: '16px' }}
          textType={'link'}
        />
      ) : null}
      <SocialLink
        user={profile.viewedUser}
        edit={change}
        register={register}
        errors={errors}
      />
      {change ? (
        <>
          <div className={styles.info}>
            Please note, that saved information will be publicly visible.
          </div>
          <div className={styles.saveBlock}>
            <div
              className={styles.cancelButton}
              onClick={() => setChange(false)}
            >
              Cancel
            </div>
            <Button
              type={'submit'}
              style={{
                background: 'linear-gradient(#2E2B63, #2E2B63)',
                boxShadow: '0px 7px 25px rgba(0, 0, 0, 0.0563538)',
                borderRadius: 11,
                width: '92px',
                height: '40px',
              }}
              styleText={{ color: '#ffffff' }}
              btnStyle="classic"
              text="Save"
            />
          </div>
        </>
      ) : null}
    </form>
  )
})

UserInfoContact.displayName = 'UserInfoContact'

export default UserInfoContact
